<template>
  <div class="vh-100 overflow-hidden d-flex flex-column justify-space-between">
    <Header ref="header">
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      <div class="header-title">{{ title }}</div>
    </Header>
    <div ref="chatContent" class="chat-content d-flex flex-column">
      <div
        v-if="application"
        class="flex-shrink-0 bg-gray-06 px-3 py-2"
        ref="application"
        data-testid="schedule"
      >
        <div class="schedule-wrapper">
          <div class="border border-outline-color">
            <button
              class="w-100 text-center expand-button"
              data-testid="expand-button"
              @click="switchExpandOrder"
            >
              <span class="text-size-16">
                勤務予定を{{ isOrderExpanded ? "閉じる" : "開く" }}
              </span>
              <div class="expand-button-icon">
                <FeatherIcon
                  v-if="isOrderExpanded"
                  icon="chevron-up"
                  class="mr-1"
                />
                <FeatherIcon v-else icon="chevron-down" class="mr-1" />
              </div>
            </button>
          </div>
          <ApplicationCard
            v-if="isOrderExpanded"
            class="application-card"
            :application="application"
            :isSimpleDisplay="true"
          />
        </div>
      </div>
      <div
        ref="messageArea"
        class="message-area flex-grow-1"
        :style="messageAreaStyles"
        @scroll="onTalkScroll"
      >
        <div class="d-flex flex-column-reverse">
          <div
            v-for="message in messageList"
            :key="'message' + message.id"
            :class="
              'senderType' in message && message.senderType === 'worker'
                ? 'message-item self'
                : 'message-item'
            "
            data-testid="message"
          >
            <div class="d-flex align-items-end">
              <div
                class="message-item__avatar"
                v-if="message.senderType === 'staff'"
                data-testid="staff-avatar"
              >
                <img src="@/assets/svg/BrandAvatar.svg?webp" />
              </div>
              <div
                class="message-item__avatar"
                v-if="message.senderType === 'office'"
                data-testid="office-avatar"
              >
                <img :src="office?.managerAvatar" />
              </div>
              <div v-if="isRead(message)" class="message-item__is-read">
                既読
              </div>
              <div :class="getMessageItemContentClass(message)">
                <p
                  class="message-item__text text-size-14 mb-2 text-break"
                  data-testid="text"
                  v-html="linkifyText(message.text)"
                />
                <p class="message-item__time text-size-12" data-testid="date">
                  {{ formatFromISOString(message.createdAt) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <button
          v-if="!isPageAtBottom"
          class="arrow-to-bottom"
          @click="scrollBottomMessage"
        >
          <FeatherIcon icon="arrow-down" />
        </button>
      </div>
    </div>

    <div
      ref="footer"
      class="fixed-footer fixed-bottom flex-shrink-0 overflow-hidden bg-gray-06 px-3 pt-1 relative"
    >
      <CustomValidationObserver ref="vObserver" v-slot="{ handleSubmit }">
        <BForm class="d-flex align-items-end">
          <ValidationProvider
            class="flex-grow-1 align-items-center"
            v-slot="validationState"
            name="コメント"
            :rules="{ max: 2000 }"
            v-model="inputText"
          >
            <TextAreaGroup
              v-model="inputText"
              type="text"
              :rows="2"
              :maxRows="5"
              :validationState="validationState"
              :enableTextareaAutoResize="true"
            />
          </ValidationProvider>

          <button
            type="button"
            class="d-block ml-2 mb-1 flex-shrink-0"
            @click="handleSubmit(onClickSubmitButton)"
          >
            <div class="send-button">
              <FeatherIcon icon="send" />
            </div>
          </button>
        </BForm>
      </CustomValidationObserver>
    </div>
  </div>
</template>
<script lang="ts">
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import ApplicationCard from "@/components/organisms/applications/ApplicationCard.vue";
import TextAreaGroup from "@/components/molecules/form/TextAreaGroup.vue";
import { officeService } from "@/app/services/OfficeService";
import { messageService } from "@/app/services/MessageService";
import { applicationService } from "@/app/services/ApplicationService";
import { authStore } from "@/store/auth";
import { scrollToBottom } from "@/utils/scrollUtil";
import { Component, Vue } from "vue-facing-decorator";
import { Office } from "@/types/office/Office";
import { Application } from "@/types/order/Application";
import { ApplicationMessage } from "@/types/order/ApplicationMessage";
import {
  addMilliseconds,
  formatFromUnixTimestamp,
  parseISO,
  formatFromISOString
} from "@/utils/date";
import { routerReplace } from "@/utils/routerReplace";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { linkifyText } from "@/utils/stringUtil";
import { accountService } from "@/app/services/AccountService";

const FETCH_MESSAGE_COUNT = 15;

@Component({
  components: {
    FeatherIcon,
    Header,
    ApplicationCard,
    TextAreaGroup
  }
})
export default class MessageRoomDetail extends Vue {
  office: Office | null = null;
  application: Application | null = null;
  messageList: ApplicationMessage[] = [];
  hasNext = false;
  isFetching = false;
  messageAreaRef: HTMLElement | null = null;
  inputText = "";
  fetchIntervalId: number | null = null;
  footerHeight = 0;
  isPageAtBottom = false;
  // 勤務予定が展開済みか
  isOrderExpanded = true;

  get officeId() {
    return Number(this.$route.params.officeId);
  }

  switchExpandOrder() {
    this.isOrderExpanded = !this.isOrderExpanded;
  }

  beforeCreate() {
    this.$loading.show();
  }

  created() {
    // cordova-plugin-ionic-keyboard のキーボード出現イベントを監視
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    window.addEventListener("native.keyboardshow", (event: any) => {
      // メッセージ履歴の最下部にスクロールされているであれば、
      // キーボード出現時に最下部にスクロールする(キーボードでメッセージが隠れないようにする)
      this.updateIsPageAtBottom(event.keyboardHeight);
      if (this.isPageAtBottom) this.jumpBottomMessage();
    });
  }

  async mounted() {
    // 各コンテンツの高さを設定
    this.setHeights();

    try {
      if (!(await this.hasMessageRoom())) {
        await this.$bvModal.msgBoxOk("メッセージルームが存在しません", {
          centered: true
        });
        await routerReplace(this, { name: "MessageRooms" });
        return;
      }

      await this.fetchOffice();
      await Promise.all([this.fetchApplication(), this.fetchMessage()]);
      await this.bulkReadMessages();

      this.messageAreaRef = this.$refs.messageArea as HTMLElement;

      // 5秒ごとに最新のメッセージを取得
      this.fetchIntervalId = window.setInterval(() => {
        this.fetchNewMessage();
      }, 5000);

      // フォント読み込み後に実行
      document.fonts.ready.then(() => {
        this.$nextTick(() => {
          this.jumpBottomMessage();
        });
      });

      this.updateIsPageAtBottom();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }
  unmounted() {
    if (this.fetchIntervalId) {
      clearInterval(this.fetchIntervalId);
    }
  }
  isRead(message: ApplicationMessage): boolean {
    return message.senderType === "worker" && message.isRead;
  }
  linkifyText(text: string): string {
    return linkifyText(text);
  }
  formatFromISOString(date: string) {
    return formatFromISOString(date);
  }
  onClickBack() {
    this.$router.back();
  }
  async onTalkScroll() {
    if (this.isFetching) return;

    try {
      if (
        this.messageAreaRef &&
        this.messageAreaRef.scrollTop < 500 &&
        this.hasNext
      ) {
        await this.fetchMessage();
      }
      this.updateIsPageAtBottom();
    } catch (error) {
      await showErrorModal(this, error as ErrorType);
    }
  }
  async onClickSubmitButton() {
    if (!this.inputText) return;

    try {
      this.$loading.show();
      await this.postMessage();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }
  toggleOrderCard() {
    this.$root!.$emit("bv::toggle::collapse", "orderCollapseOn");
    this.$root!.$emit("bv::toggle::collapse", "orderCollapseOff");
  }
  // 事務局ではなく、workerの返信のみクラス色付け
  getMessageItemContentClass(message: ApplicationMessage) {
    let className = "message-item__content border";

    if ("senderType" in message && message.senderType === "worker") {
      className += " bg-blue border-blue";
    } else {
      className += " bg-white border-outline-color";
    }
    return className;
  }
  async fetchOffice() {
    this.office = await officeService.fetchOffice(this.officeId);
  }
  async fetchMessage() {
    this.isFetching = true;
    const messageList = await messageService.fetchMessages({
      worker: authStore.user?.id,
      office: this.office?.id,
      limit: FETCH_MESSAGE_COUNT,
      offset: this.messageList.length,
      ordering: "-created_at"
    });
    // 初回時のみ一番下までジャンプ
    if (this.messageList.length === 0) {
      this.messageList = messageList.results;
      this.$nextTick(() => {
        this.jumpBottomMessage();
      });
    } else {
      this.messageList = this.messageList.concat(messageList.results);
    }
    this.hasNext = !!messageList.next;
    this.isFetching = false;
  }
  // 保持しているメッセージより新しいメッセージを取得
  async fetchNewMessage() {
    if (this.isFetching) return;
    this.isFetching = true;
    // 現時点でメッセージがない場合は、gteをundefinedに
    let latestDate = undefined;
    if (this.messageList.length > 0) {
      // 今保持している最新の+1msをgteにセット
      latestDate = formatFromUnixTimestamp(
        addMilliseconds(parseISO(this.messageList[0].createdAt), 1),
        "yyyy-MM-dd HH:mm:ss.SSS"
      );
    }
    const messageList = await messageService.fetchMessages({
      worker: authStore.user?.id,
      office: this.office?.id,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      created_at__gte: latestDate,
      ordering: "-created_at"
    });
    if (messageList.count > 0) {
      this.addMessagesByNotDistinction(messageList.results);

      // 既読にする
      await this.bulkReadMessages();

      // 新着取得時は、DOM更新を待ってから一番下までジャンプ
      this.$nextTick(() => {
        this.jumpBottomMessage();
      });
    }
    this.isFetching = false;
  }
  async fetchApplication() {
    if (!authStore.user?.id) return;
    this.application = await applicationService.fetchNextScheduledApplication(
      authStore.user.id,
      this.office?.id
    );
  }
  async postMessage() {
    if (authStore.user?.id && this.office?.id) {
      const message = await messageService.createMessage({
        text: this.inputText,
        worker: authStore.user.id,
        office: this.office.id
      });
      this.addMessagesByNotDistinction([message]);
    }
    this.inputText = "";
    this.scrollBottomMessage();
    this.$nextTick(() => {
      this.scrollBottomMessage();
    });
  }

  get latestMessageId(): number | null {
    return this.messageList.length > 0
      ? Math.max(...this.messageList.map(m => m.id))
      : null;
  }

  /** ルームの通知すべてに既読を付ける */
  async bulkReadMessages() {
    if (!this.office?.id || !authStore.user?.id || !this.latestMessageId) {
      return;
    }
    await messageService.readMessages(
      authStore.user.id,
      this.office.id,
      this.latestMessageId
    );
    // アプリバッジ件数を最新化する
    await accountService.refreshAppBadgeCount();
  }

  // メッセージの一番下までジャンプ
  jumpBottomMessage() {
    if (!this.messageAreaRef) return;
    this.messageAreaRef.scrollTop = this.messageAreaRef.scrollHeight;
  }
  // メッセージの一番下までスムーススクロール
  scrollBottomMessage() {
    if (!this.messageAreaRef) return;
    scrollToBottom(this.messageAreaRef, { duration: 500 });
  }
  // IDの重複なしでメッセージを追加する
  addMessagesByNotDistinction(messages: ApplicationMessage[]) {
    const displayingMessageIdList = this.messageList.map(m => m.id);

    // 新しいIDのメッセージのみを表示させる
    const notDuplicatedMessages = messages.filter(m => {
      return !displayingMessageIdList.includes(m.id);
    });
    this.messageList.unshift(...notDuplicatedMessages);
  }
  get title() {
    return this.office ? this.office.name : "...";
  }
  // コンテンツの高さ指定関連
  setHeights() {
    if (this.$refs.footer) {
      this.footerHeight = (this.$refs.footer as HTMLDivElement).clientHeight;
    }
  }
  get messageAreaStyles() {
    return {
      marginBottom: `${this.footerHeight}px`
    };
  }

  /**
   * ページの最下部までスクロールされているかどうかを返す
   * @param keyboardHeight 表示されているキーボードの高さ
   */
  updateIsPageAtBottom(keyboardHeight = 0) {
    // メッセージエリアのrefが設定されていない場合は、最下部までスクロールされているとみなす
    if (!this.messageAreaRef) {
      this.isPageAtBottom = true;
      return;
    }
    const windowHeight = window.innerHeight;
    const areaHeight = this.messageAreaRef?.scrollHeight || 0;
    const areaTop = this.messageAreaRef?.scrollTop || 0;
    const inScreenMessageAreaHeight = areaHeight - areaTop;
    const screenHeight = windowHeight + keyboardHeight - this.footerHeight;
    this.isPageAtBottom = screenHeight >= inScreenMessageAreaHeight;
  }
  async hasMessageRoom() {
    const { result } = await messageService.hasMessageRoom(this.officeId);
    return result;
  }
}
</script>
<style lang="scss" scoped>
.chat-content {
  overflow-y: auto;
}

.back-button {
  position: absolute;
  top: 10px;
  left: 10px;
}

.message-area {
  padding: 8px 16px 50px;
  overflow: auto;
}

.message-item {
  padding: 0 24px 0 0;
  margin: 12px auto 0 0;

  :deep(a) {
    text-decoration: underline;
  }

  &:not(.self) {
    :deep(a) {
      color: $blue;
    }
  }

  &.self {
    padding: 0 0 0 24px;
    margin: 12px 0 0 auto;

    p {
      color: $white;
    }
  }

  &__content {
    padding: 8px;
    border-radius: 12px;
  }

  &__time {
    opacity: 0.5;
  }

  &__text {
    white-space: pre-wrap;
  }

  &__is-read {
    min-width: 32px;
    padding-right: 8px;
    padding-bottom: 8px;
    font-size: 12px;
    color: $mild;
  }

  &__avatar {
    flex-shrink: 0;
    align-self: flex-start;
    width: 40px;
    height: 40px;
    margin-right: 8px;
    overflow: hidden;
    border-radius: 20px;
    box-shadow: 0 0 0 1px $gray-04;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}

.fixed-footer {
  z-index: 0;
  padding-bottom: env(safe-area-inset-bottom);

  button {
    border-radius: 50%;
    outline: none;

    &:focus {
      box-shadow: 0 0 8px rgba($blue, 0.75);
    }
  }
}

:deep(.fixed-footer textarea) {
  padding: 4px 8px;
}

.send-button {
  padding: 10px;
  color: $white;
  background-color: $blue;
  border-radius: 50%;
}

.arrow-to-bottom {
  position: absolute;
  right: 5px;
  bottom: 100px;
  z-index: 100;
  padding: 8px;
  cursor: pointer;
  background-color: rgba($white, 0.5);
  border-radius: 50%;
  box-shadow: 0 0 8px rgba($black, 0.25);
}

.header-title {
  display: -webkit-box;
  padding-right: 40px;
  padding-left: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.schedule-wrapper {
  position: relative;

  .expand-button {
    padding: 12px 16px;
    background-color: $white;
    border-radius: 4px;
  }

  .expand-button-icon {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 24px;
    height: 24px;
  }
}
</style>
